import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { toast } from "@/hooks/use-toast";

const SalonWaitlistCard = () => {
  const queryClient = useQueryClient();
  
  const { data: waitlistRequests } = useQuery({
    queryKey: ["user-salon-waitlist"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from("salon_waitlist")
        .select(`
          *,
          events (
            id,
            title,
            start_time,
            venue
          )
        `)
        .eq("user_id", user.id)
        .in("status", ["pending", "invited"])
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const cancelRequestMutation = useMutation({
    mutationFn: async (requestId: string) => {
      const { error } = await supabase
        .from("salon_waitlist")
        .delete()
        .eq("id", requestId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-salon-waitlist"] });
      toast({
        title: "Request cancelled",
        description: "Your waitlist request has been cancelled.",
      });
    },
    onError: (error) => {
      console.error("Error cancelling request:", error);
      toast({
        title: "Error",
        description: "Failed to cancel request. Please try again.",
        variant: "destructive",
      });
    },
  });

  if (!waitlistRequests || waitlistRequests.length === 0) {
    return null;
  }

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      pending: "secondary",
      invited: "outline",
      booked: "default",
      declined: "destructive",
    };
    return (
      <Badge variant={variants[status] || "outline"}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const getStatusMessage = (status: string) => {
    const messages: Record<string, string> = {
      pending: "We're reviewing requests and will notify you soon.",
      invited: "You're invited! Complete your booking now.",
      booked: "You're all set! See you there.",
      declined: "Unfortunately, we couldn't allocate a spot this time.",
    };
    return messages[status] || "";
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Secret Salon Waitlist</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {waitlistRequests.map((request) => (
          <div key={request.id} className="border rounded-lg p-4 space-y-2">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-semibold">{request.events?.title}</h3>
                <p className="text-sm text-muted-foreground">
                  {request.events?.start_time && format(new Date(request.events.start_time), "MMM d, yyyy")}
                </p>
                <p className="text-sm text-muted-foreground">
                  {request.events?.venue}
                </p>
              </div>
              {getStatusBadge(request.status)}
            </div>
            
            <p className="text-sm text-muted-foreground">
              {getStatusMessage(request.status)}
            </p>

            {request.status === "invited" && (
              <Button asChild className="w-full">
                <Link to={`/events/${request.events?.id}?invited=true`}>
                  Complete Booking
                </Link>
              </Button>
            )}

            {request.status === "pending" && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => cancelRequestMutation.mutate(request.id)}
                disabled={cancelRequestMutation.isPending}
              >
                Cancel Request
              </Button>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default SalonWaitlistCard;